import {
	AlertTriangle,
	ClipboardCheck,
	Eye,
	MessageSquareMore,
	Target,
	Users,
} from 'lucide-react'
import { usePersonCommitmentSheet } from '@/api/queries/commitment-matrix-queries'
import { InspectionsSummaryCard } from '@/components/inspections-summary-card'
import { Skeleton } from '@/components/ui/skeleton'
import { ROUTES } from '@/constants/routes'
import {
	type InspectionKind,
	usePersonalInspectionsSummary,
} from '@/hooks/use-personal-inspections-summary'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store'

const KIND_CONFIG: Record<
	InspectionKind,
	{
		icon: React.ReactNode
		iconClass: string
		hoverClass: string
		borderClass: string
		route: string
		label: string
	}
> = {
	directed: {
		icon: <ClipboardCheck className="size-4" />,
		iconClass: 'bg-kaefer-ocean-blue',
		hoverClass: 'hover:bg-kaefer-ocean-blue/10',
		borderClass: 'border-l-kaefer-ocean-blue',
		route: ROUTES.INSPECTIONS.DIRECTED.INDEX,
		label: 'Direcionada',
	},
	seeAndAct: {
		icon: <Eye className="size-4" />,
		iconClass: 'bg-kaefer-grass-green',
		hoverClass: 'hover:bg-kaefer-grass-green/10',
		borderClass: 'border-l-kaefer-grass-green',
		route: ROUTES.INSPECTIONS.SEE_AND_ACT.INDEX,
		label: 'Ver e Agir',
	},
	yellowCard: {
		icon: <AlertTriangle className="size-4" />,
		iconClass: 'bg-kaefer-sunny-yellow',
		hoverClass: 'hover:bg-kaefer-sunny-yellow/10',
		borderClass: 'border-l-kaefer-sunny-yellow',
		route: ROUTES.INSPECTIONS.YELLOW_CARD.INDEX,
		label: 'Cartão Amarelo',
	},
	interaction: {
		icon: <Users className="size-4" />,
		iconClass: 'bg-kaefer-violet',
		hoverClass: 'hover:bg-kaefer-violet/10',
		borderClass: 'border-l-kaefer-violet',
		route: ROUTES.INSPECTIONS.INTERACTION.INDEX,
		label: 'Interação',
	},
	dds: {
		icon: <MessageSquareMore className="size-4" />,
		iconClass: 'bg-kaefer-honey-yellow',
		hoverClass: 'hover:bg-kaefer-honey-yellow/10',
		borderClass: 'border-l-kaefer-honey-yellow',
		route: ROUTES.DDS.INDEX,
		label: 'DDS',
	},
}

const KIND_ORDER: InspectionKind[] = [
	'seeAndAct',
	'directed',
	'interaction',
	'yellowCard',
	'dds',
]

function getGreeting(): string {
	const hour = new Date().getHours()
	if (hour < 12) return 'Bom dia'
	if (hour < 18) return 'Boa tarde'
	return 'Boa noite'
}

export function PersonalDashboard() {
	const user = useAuthStore((s) => s.user)
	const summary = usePersonalInspectionsSummary()

	const firstName = user?.name?.split(' ')[0] ?? ''

	/* ── matriz de comprometimento pessoal ────────────────────────────── */
	const { data: sheet, isLoading: isLoadingMatrix } = usePersonCommitmentSheet(
		user?.userId ?? null,
	)
	const progress = sheet?.progress
	const fullyAboned = progress?.status === 'ABONED'
	const pct =
		progress && progress.target > 0
			? Math.min(100, Math.round((progress.done / progress.target) * 100))
			: 0

	const totalDelta = summary.totalCurrentMonth - summary.totalPreviousMonth

	return (
		<div className="mb-8 flex flex-col gap-4">
			{/* Saudação */}
			<div>
				<h1 className="text-2xl font-bold text-kaefer-dark-grey sm:text-3xl">
					{getGreeting()}
					{firstName ? `, ${firstName}` : ''}!
				</h1>
				<p className="mt-1 text-sm text-muted-foreground">
					Aqui está o resumo da sua atividade neste mês.
				</p>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				{/* Matriz de Comprometimento pessoal */}
				<div className="rounded-lg border border-kaefer-light-silver bg-white p-4 lg:col-span-1">
					<div className="flex items-center gap-2">
						<Target className="size-4 text-kaefer-medium-grey" />
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							Sua meta de inspeções
						</span>
					</div>

					{isLoadingMatrix ? (
						<div className="mt-3 flex flex-col gap-2">
							<Skeleton className="h-8 w-24" />
							<Skeleton className="h-2 w-full" />
						</div>
					) : fullyAboned ? (
						<>
							<div className="mt-3 flex items-end justify-between">
								<span className="text-3xl font-bold text-kaefer-dark-grey">
									0
									<span className="text-lg font-medium text-muted-foreground">
										/0
									</span>
								</span>
							</div>
							<span className="mt-2 inline-flex rounded-full bg-kaefer-sapphire-blue/15 px-2 py-0.5 text-[11px] font-medium text-kaefer-sapphire-blue">
								{progress?.abonedDays} dia
								{progress?.abonedDays === 1 ? '' : 's'} abonado
								{progress?.abonedDays === 1 ? '' : 's'} — dispensado este mês
							</span>
						</>
					) : progress && progress.target > 0 ? (
						<>
							<div className="mt-3 flex items-end justify-between">
								<span className="text-3xl font-bold text-kaefer-dark-grey">
									{progress.done}
									<span className="text-lg font-medium text-muted-foreground">
										/{progress.target}
									</span>
								</span>
								<span
									className={cn(
										'text-sm font-semibold',
										pct >= 100
											? 'text-kaefer-forest-green'
											: 'text-kaefer-dark-grey',
									)}
								>
									{pct}%
								</span>
							</div>
							<div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-kaefer-light-silver/60">
								<div
									className={cn(
										'h-full rounded-full transition-all',
										pct >= 100
											? 'bg-kaefer-grass-green'
											: 'bg-kaefer-medium-grey',
									)}
									style={{ width: `${pct}%` }}
								/>
							</div>
							{progress.abonedDays > 0 && (
								<span className="mt-2 inline-flex rounded-full bg-kaefer-sapphire-blue/15 px-2 py-0.5 text-[11px] font-medium text-kaefer-sapphire-blue">
									{progress.abonedDays} dia
									{progress.abonedDays === 1 ? '' : 's'} abonado
									{progress.abonedDays === 1 ? '' : 's'} este mês
								</span>
							)}
						</>
					) : (
						<p className="mt-3 text-sm text-muted-foreground">
							Nenhuma meta de inspeções definida para você este mês.
						</p>
					)}
				</div>

				{/* Total do mês */}
				<InspectionsSummaryCard
					isLoading={summary.isLoading}
					kindOrder={KIND_ORDER}
					kindConfig={KIND_CONFIG}
					byKind={summary.byKind}
					totalDelta={totalDelta}
				/>
			</div>
		</div>
	)
}
